import { storage } from '../firebase/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Sube una imagen a Firebase Storage y retorna la URL de descarga
 * @param {File} file - Archivo de imagen a subir
 * @param {string} folder - Carpeta donde guardar la imagen (opcional)
 * @param {function} onProgress - Callback para mostrar progreso (opcional)
 * @returns {Promise<string>} URL de descarga de la imagen
 */
export const uploadImageToFirebase = async (file, folder = 'editor-images', onProgress = null) => {
  try {
    // Validar que sea un archivo de imagen
    if (!file.type.startsWith('image/')) {
      throw new Error('El archivo debe ser una imagen');
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('La imagen no puede ser mayor a 5MB');
    }

    // Generar nombre único para el archivo
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${randomString}.${fileExtension}`;

    // Crear referencia en Firebase Storage
    const imageRef = storageRef(storage, `${folder}/${fileName}`);

    // Subir archivo
    const uploadResult = await uploadBytes(imageRef, file);
    
    // Obtener URL de descarga
    const downloadURL = await getDownloadURL(uploadResult.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error al subir imagen:', error);
    throw error;
  }
};

/**
 * Valida si un archivo es una imagen válida
 * @param {File} file - Archivo a validar
 * @returns {boolean} True si es una imagen válida
 */
export const isValidImage = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB
  
  return validTypes.includes(file.type) && file.size <= maxSize;
};

/**
 * Redimensiona una imagen antes de subirla (opcional)
 * @param {File} file - Archivo de imagen
 * @param {number} maxWidth - Ancho máximo
 * @param {number} maxHeight - Alto máximo
 * @param {number} quality - Calidad de compresión (0-1)
 * @returns {Promise<File>} Archivo redimensionado
 */
export const resizeImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calcular nuevas dimensiones manteniendo proporción
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Dibujar imagen redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Convertir a blob
      canvas.toBlob(
        (blob) => {
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        file.type,
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Maneja la subida de múltiples imágenes
 * @param {FileList} files - Lista de archivos
 * @param {string} folder - Carpeta de destino
 * @param {function} onProgress - Callback de progreso
 * @returns {Promise<Array<string>>} Array de URLs de descarga
 */
export const uploadMultipleImages = async (files, folder = 'editor-images', onProgress = null) => {
  const uploadPromises = Array.from(files).map(async (file, index) => {
    try {
      const url = await uploadImageToFirebase(file, folder, (progress) => {
        if (onProgress) {
          onProgress(index, progress, files.length);
        }
      });
      return { success: true, url, fileName: file.name };
    } catch (error) {
      return { success: false, error: error.message, fileName: file.name };
    }
  });

  return Promise.all(uploadPromises);
};